import {
  faCalendar,
  faChevronDown,
  faChevronUp,
  faMap,
  faMountain,
  faSearch,
  faTree,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useForm } from "@tanstack/react-form";
import { useEffect, useState } from "react";
import { Button, Form, InputGroup, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { environementIcons, environementLabels } from "../model/environment";
import type {
  CampsiteCalendarManagement,
  CampsiteEnvironment,
  CampsiteFilter as CampsiteFilterInput,
} from "../types/graphql/graphql";
import styles from "./CampsiteFilter.module.css";
import { ChipGroup } from "./ChipGroup";

type CampsiteFilterProps = {
  onFilter: (report: CampsiteFilterInput) => void;
};

export function CampsiteFilter({ onFilter }: CampsiteFilterProps) {
  const form = useForm({
    defaultValues: { calendarManagements: ["full"] } as CampsiteFilterInput,
    onSubmit: (props) => {
      onFilter({ ...props.value, q: searchQuery });
      setExpand(false);
    },
  });

  const { t } = useTranslation();
  const [expand, setExpand] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    const timer = setTimeout(() => {
      onFilter({ q: searchQuery });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, onFilter]);

  return (
    <Stack gap={3}>
      <Stack direction="horizontal" gap={3} className={styles.searchBar}>
        <InputGroup>
          <Form.Control
            placeholder={t("activemodel.attributes.campsite/filter.q")}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
          />
          <Button variant="secondary">
            <FontAwesomeIcon icon={faSearch} />
          </Button>
        </InputGroup>
        <a href="#map">
          <FontAwesomeIcon icon={faMap} />
        </a>
        <Button variant="link" onClick={() => setExpand((expand) => !expand)}>
          <Stack direction="horizontal" gap={2}>
            {t("layouts.search.extended_search")}
            <FontAwesomeIcon icon={expand ? faChevronUp : faChevronDown} />
          </Stack>
        </Button>
      </Stack>
      {expand && (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <Stack gap={3}>
            <Form.Group>
              <Form.Label>
                <Stack direction="horizontal" gap={2}>
                  <FontAwesomeIcon icon={faTree} />
                  {t("activemodel.attributes.campsite/filter.environments")}
                </Stack>
              </Form.Label>
              <InputGroup>
                <form.Field name="environments">
                  {(field) => (
                    <ChipGroup
                      options={(
                        [
                          "car_access",
                          "env_forest",
                          "env_lake",
                          "env_meadow",
                          "env_river",
                          "env_stream",
                          "env_willow",
                        ] as CampsiteEnvironment[]
                      ).map((value) => ({
                        label: (
                          <>
                            {environementIcons[value] && <FontAwesomeIcon icon={environementIcons[value]} />}
                            {t(environementLabels[value]) ?? ""}
                          </>
                        ),
                        value,
                      }))}
                      value={field.state.value ?? []}
                      onChange={(value) => field.handleChange(value)}
                    />
                  )}
                </form.Field>
              </InputGroup>
            </Form.Group>

            <Form.Group>
              <Form.Label>
                <Stack direction="horizontal" gap={2}>
                  <FontAwesomeIcon icon={faUser} />
                  {t("activerecord.attributes.campsite.max_headcount")}
                </Stack>
              </Form.Label>
              <InputGroup>
                <form.Field name="headcountMin">
                  {(field) => (
                    <Form.Control
                      type="number"
                      placeholder={t("from")}
                      name={field.name}
                      value={field.state.value ?? undefined}
                      onChange={(e) => field.handleChange(Number.parseInt(e.target.value, 10))}
                    />
                  )}
                </form.Field>
                <InputGroup.Text>-</InputGroup.Text>
                <form.Field name="headcountMax">
                  {(field) => (
                    <Form.Control
                      type="number"
                      placeholder={t("to")}
                      name={field.name}
                      value={field.state.value ?? undefined}
                      onChange={(e) => field.handleChange(Number.parseInt(e.target.value, 10))}
                    />
                  )}
                </form.Field>
              </InputGroup>
            </Form.Group>
            <Form.Group>
              <Form.Label>
                <Stack direction="horizontal" gap={2}>
                  <FontAwesomeIcon icon={faMountain} />
                  {t("activemodel.attributes.campsite/filter.altitude")}
                </Stack>
              </Form.Label>
              <InputGroup>
                <form.Field name="altitudeMin">
                  {(field) => (
                    <Form.Control
                      type="number"
                      placeholder={t("from")}
                      name={field.name}
                      value={field.state.value ?? undefined}
                      onChange={(e) => field.handleChange(Number.parseInt(e.target.value, 10))}
                    />
                  )}
                </form.Field>
                <InputGroup.Text>-</InputGroup.Text>
                <form.Field name="altitudeMax">
                  {(field) => (
                    <Form.Control
                      type="number"
                      placeholder={t("to")}
                      name={field.name}
                      value={field.state.value ?? undefined}
                      onChange={(e) => field.handleChange(Number.parseInt(e.target.value, 10))}
                    />
                  )}
                </form.Field>
              </InputGroup>
            </Form.Group>
            <Form.Group>
              <Form.Label>
                <Stack direction="horizontal" gap={2}>
                  <FontAwesomeIcon icon={faCalendar} />
                  {t("activemodel.attributes.campsite/filter.free_between")}
                </Stack>
              </Form.Label>
              <InputGroup>
                <form.Field name="freeFrom">
                  {(field) => (
                    <Form.Control
                      type="date"
                      placeholder={t("from")}
                      name={field.name}
                      value={field.state.value ?? undefined}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  )}
                </form.Field>
                <InputGroup.Text>-</InputGroup.Text>
                <form.Field name="freeTo">
                  {(field) => (
                    <Form.Control
                      type="date"
                      placeholder={t("to")}
                      name={field.name}
                      value={field.state.value ?? undefined}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  )}
                </form.Field>
              </InputGroup>
              <InputGroup className="mt-2">
                <form.Field name="calendarManagements">
                  {(field) => (
                    <ChipGroup
                      options={(["full", "rough", "none"] as CampsiteCalendarManagement[]).map((value) => ({
                        label: t(`enums.campsite.calendar_managements.${value}`) ?? "",
                        value,
                      }))}
                      value={field.state.value ?? []}
                      onChange={(value) => field.handleChange(value)}
                    />
                  )}
                </form.Field>
              </InputGroup>
            </Form.Group>

            <Button className="mt-3" type="submit">
              {t("layouts.search.search")}
            </Button>
          </Stack>
        </Form>
      )}
    </Stack>
  );
}
